import React, { useEffect, useState } from "react";
import { growthApi } from "../api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Save, ShieldAlert, ChevronDown, ChevronUp } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const stageColor = (s) => {
  if (s === "READY") return "bg-emerald-100 text-emerald-700 border-emerald-200";
  if (s === "NOT ELIGIBLE") return "bg-red-100 text-red-700 border-red-200";
  if (s === "NURTURE") return "bg-slate-100 text-slate-600 border-slate-200";
  if (s === "NEW FAMILY INQUIRY") return "bg-amber-100 text-amber-700 border-amber-200";
  return "bg-blue-100 text-blue-700 border-blue-200";
};

const CandidateCard = ({ cand, stages, checklistKeys, results, onSaved }) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(cand);
  const [busy, setBusy] = useState(false);

  const setStage = (v) => {
    if (v === "READY" && !window.confirm("Compliance check: READY must never be set solely from website answers. Confirm the eligibility checklist and SSA/case-manager review are complete?")) return;
    setEdit((e) => ({ ...e, stage: v }));
  };

  const toggleCheck = (k) =>
    setEdit((e) => ({ ...e, checklist: { ...(e.checklist || {}), [k]: !(e.checklist || {})[k] } }));

  const save = async () => {
    setBusy(true);
    try {
      const res = await growthApi.fcUpdate(cand.id, {
        stage: edit.stage, caregiverName: edit.caregiverName, checklist: edit.checklist || {},
        result: edit.result ?? "", notes: edit.notes ?? "",
      });
      onSaved(res.data);
      toast({ title: "Saved", description: `${cand.clientName} → ${res.data.stage}` });
    } catch {
      toast({ title: "Save failed", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const checked = Object.values(edit.checklist || {}).filter(Boolean).length;

  return (
    <div data-testid={`fc-candidate-${cand.id}`} className="bg-white rounded-2xl border border-slate-100 shadow-sm">
      <button className="w-full flex items-center justify-between gap-2 px-5 py-4 text-left" onClick={() => setOpen((o) => !o)}>
        <div className="flex items-center gap-3 flex-wrap min-w-0">
          <span className="font-semibold text-[#0E2A33]">{cand.clientName}</span>
          <span className="text-xs text-slate-400">{cand.county ? `${cand.county} County` : "County —"} · {cand.program || "Program unsure"}</span>
          <Badge className={`border text-[11px] ${stageColor(edit.stage)}`}>{edit.stage}</Badge>
          {edit.result && <Badge className="border text-[11px] bg-violet-100 text-violet-700 border-violet-200">{edit.result}</Badge>}
          <span className="text-[11px] text-slate-400">Checklist {checked}/{checklistKeys.length}</span>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />}
      </button>

      {open && (
        <div className="px-5 pb-5 border-t border-slate-100 pt-4 space-y-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
            <div><span className="text-slate-400 text-xs block">Phone</span>{cand.phone || "—"}</div>
            <div><span className="text-slate-400 text-xs block">Email</span>{cand.email || "—"}</div>
            <div><span className="text-slate-400 text-xs block">Caregiver interested</span>{cand.hasCaregiver || "—"} {cand.relationship && `(${cand.relationship})`}</div>
            <div><span className="text-slate-400 text-xs block">SSA / case manager</span>{cand.hasSSA || "—"} · Age: {cand.ageGroup || "—"}</div>
          </div>
          <div className="text-sm"><span className="text-slate-400 text-xs block">Requested services</span>{(cand.services || []).join(", ") || "—"} · Preferred contact: {cand.preferredContact || "—"}</div>

          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Pipeline Stage</Label>
              <Select value={edit.stage} onValueChange={setStage}>
                <SelectTrigger data-testid={`fc-stage-select-${cand.id}`} className="mt-1 h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{stages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Proposed Caregiver (linked worker)</Label>
              <Input value={edit.caregiverName || ""} onChange={(e) => setEdit((x) => ({ ...x, caregiverName: e.target.value }))} className="mt-1 h-9" placeholder="Name of proposed caregiver" />
            </div>
            <div>
              <Label className="text-xs">Screening Result</Label>
              <Select value={edit.result || "none"} onValueChange={(v) => setEdit((x) => ({ ...x, result: v === "none" ? "" : v }))}>
                <SelectTrigger data-testid={`fc-result-select-${cand.id}`} className="mt-1 h-9"><SelectValue placeholder="Not set" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not set</SelectItem>
                  {results.filter(Boolean).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs">Eligibility Checklist (internal — never auto-approves)</Label>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 mt-2">
              {checklistKeys.map((k) => (
                <label key={k} className="flex items-center gap-2 text-xs text-slate-700 bg-[#F7FBFC] rounded-lg border border-slate-100 px-2.5 py-2 cursor-pointer">
                  <Checkbox checked={!!(edit.checklist || {})[k]} onCheckedChange={() => toggleCheck(k)} />
                  {k}
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea value={edit.notes || ""} onChange={(e) => setEdit((x) => ({ ...x, notes: e.target.value }))} className="mt-1 text-sm" rows={2} />
          </div>

          <div className="flex justify-end">
            <Button data-testid={`fc-save-${cand.id}`} size="sm" disabled={busy} onClick={save} className="bg-[#0E5A72] hover:bg-[#0A4557] text-white rounded-full px-5">
              <Save className="mr-2 h-4 w-4" /> Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const FCPipeline = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    growthApi.fcList().then((r) => setData(r.data)).catch(() => {});
  }, []);

  const onSaved = (updated) =>
    setData((d) => ({ ...d, candidates: d.candidates.map((c) => (c.id === updated.id ? updated : c)) }));

  if (!data) return <div className="text-center text-slate-400 py-10">Loading family caregiver pipeline…</div>;

  return (
    <div data-testid="fc-pipeline" className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
        <ShieldAlert className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="text-sm text-amber-800">
          <span className="font-semibold">Compliance:</span> never mark an inquiry READY solely from website answers.
          Every arrangement requires waiver verification, SSA/case-manager review and worker screening. Client and
          proposed-caregiver details stay confidential — never publish them.
        </div>
      </div>
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-xl text-[#0E2A33]">Family Caregiver Pipeline</h3>
        <span className="text-sm text-slate-500">{data.candidates.length} inquiries</span>
      </div>
      <div className="space-y-3">
        {data.candidates.map((c) => (
          <CandidateCard key={c.id} cand={c} stages={data.stages} checklistKeys={data.checklistKeys} results={data.results} onSaved={onSaved} />
        ))}
        {data.candidates.length === 0 && (
          <div className="text-center text-slate-400 py-12 bg-white rounded-2xl border border-slate-100">
            No family caregiver inquiries yet. They arrive from myohcare.com/family-caregiver.
          </div>
        )}
      </div>
    </div>
  );
};

export default FCPipeline;
